import { memo, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { ExternalLink, Globe } from 'lucide-react';
import { useWebsites } from '@/hooks/use-content';
import type { WebsiteRow as WebsiteItem } from '@/types/database';
import SectionEyebrow from './SectionEyebrow';

const WebsiteCardSkeleton = () => (
  <div className="rounded-2xl overflow-hidden border border-border bg-card">
    <div className="flex items-center gap-2 px-4 py-3 bg-secondary/60 border-b border-border">
      <Skeleton className="h-3 w-3 rounded-full shrink-0" />
      <Skeleton className="h-3 w-3 rounded-full shrink-0" />
      <Skeleton className="h-3 w-3 rounded-full shrink-0" />
      <Skeleton className="h-5 flex-1 ml-3 rounded-md" />
    </div>
    <Skeleton className="w-full rounded-none" style={{ aspectRatio: '1 / 0.75' }} />
    <div className="p-6 space-y-2">
      <Skeleton className="h-5 w-1/2" />
      <Skeleton className="h-4 w-3/4" />
    </div>
  </div>
);

// Reference desktop width the live site is rendered at, then scaled down to
// fit the card exactly — so the full page width is always visible, never cropped.
const PREVIEW_WIDTH = 1440;
const PREVIEW_ASPECT = 3 / 4; // height = width * PREVIEW_ASPECT

const WebsitePreview = ({ url, title }: { url: string; title: string }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateScale = () => {
      if (el.clientWidth > 0) setScale(el.clientWidth / PREVIEW_WIDTH);
    };

    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="relative w-full overflow-hidden bg-muted" style={{ aspectRatio: `1 / ${PREVIEW_ASPECT}` }}>
      <iframe
        src={url}
        title={title}
        loading="lazy"
        referrerPolicy="no-referrer"
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
        className="absolute top-0 left-0 border-0 pointer-events-none"
        style={{
          width: `${PREVIEW_WIDTH}px`,
          height: `${PREVIEW_WIDTH * PREVIEW_ASPECT}px`,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      />
    </div>
  );
};

const WebsiteCard = memo(({ site }: { site: WebsiteItem }) => {
  const { t } = useTranslation();
  const displayUrl = site.url.replace(/^https?:\/\//, '').replace(/\/$/, '');

  return (
    <div className="group relative rounded-2xl overflow-hidden border border-border bg-card shadow-card hover:shadow-soft hover:-translate-y-1.5 transition-all duration-300">
      {/* Browser chrome */}
      <div className="flex items-center gap-2 px-4 py-3 bg-secondary/60 border-b border-border">
        <span className="h-3 w-3 rounded-full bg-destructive/70" />
        <span className="h-3 w-3 rounded-full bg-yellow-500/70" />
        <span className="h-3 w-3 rounded-full bg-green-500/70" />
        <div className="ml-3 flex-1 flex items-center gap-2 px-3 py-1 rounded-md bg-background/60 text-xs text-muted-foreground truncate">
          <Globe className="h-3 w-3 shrink-0" />
          <span className="truncate">{displayUrl}</span>
        </div>
      </div>

      {/* Live embedded preview — full page width, scaled to fit */}
      <WebsitePreview url={site.url} title={site.name} />

      <div className="p-6 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold truncate">{site.name}</h3>
          <p className="text-sm text-muted-foreground truncate">{site.description}</p>
        </div>
        <a
          href={site.url}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-accent transition-colors"
          aria-label={t('websites.visitAria', { name: site.name })}
        >
          {t('websites.visit')} <ExternalLink className="h-4 w-4" />
        </a>
      </div>
    </div>
  );
});
WebsiteCard.displayName = 'WebsiteCard';

const Websites = () => {
  const { t } = useTranslation();
  const { data: websitesData, isLoading } = useWebsites();

  return (
    <section id="websites" className="py-14 sm:py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <SectionEyebrow index="04" label={t('websites.eyebrow')} />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('websites.title')}</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            <Trans i18nKey="websites.description" components={{ b: <strong /> }} />
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {isLoading
            ? Array.from({ length: 2 }).map((_, idx) => <WebsiteCardSkeleton key={idx} />)
            : websitesData?.map((site) => <WebsiteCard key={site.id} site={site} />)}
        </div>
      </div>
    </section>
  );
};

export default Websites;
